import React from "react";

export type ThemeMode = "light" | "dark";

type ThemeModeContextValue = {
  themeMode: ThemeMode;
  setThemeMode: (themeMode: ThemeMode) => void;
};

const THEME_MODE_STORAGE_KEY = "amtdl-theme-mode";

const ThemeModeContext = React.createContext<ThemeModeContextValue | null>(null);

const getStoredThemeMode = (): ThemeMode => {
  try {
    return localStorage.getItem(THEME_MODE_STORAGE_KEY) === "dark" ? "dark" : "light";
  } catch {
    return "light";
  }
};

export function useThemeMode() {
  const context = React.useContext(ThemeModeContext);

  if (!context) {
    throw new Error("useThemeMode must be used within ThemeModeProvider");
  }

  return context;
}

function ThemeModeProvider({ children }: React.PropsWithChildren) {
  const [themeMode, setThemeMode] = React.useState<ThemeMode>(getStoredThemeMode);

  React.useLayoutEffect(() => {
    document.documentElement.dataset.theme = themeMode;

    try {
      localStorage.setItem(THEME_MODE_STORAGE_KEY, themeMode);
    } catch {
      // Theme switching remains available when browser storage is unavailable.
    }
  }, [themeMode]);

  const contextValue = React.useMemo(
    () => ({
      themeMode,
      setThemeMode,
    }),
    [themeMode],
  );

  return <ThemeModeContext.Provider value={contextValue}>{children}</ThemeModeContext.Provider>;
}

export default ThemeModeProvider;
